import type { TabStopDefinition } from 'docx';
import { DeletedTextRun, InsertedTextRun, PositionalTab, Tab, TextRun } from 'docx';
import type { NodeEntry } from 'slate';

import type { DocumentContext } from './document-context';
import { getEditDate } from './internal';
import { isInsertTabEdit, isRemoveTabEdit } from '../../../slate-api';
import type { TabElement, TabOptions } from '../../../slate-api';

export function createTab(
  [node]: NodeEntry<TabElement>,
  { context }: { context: DocumentContext }
) {
  const insertSuggestion = node.suggestedEdits?.find(isInsertTabEdit);
  const removedSuggestion = node.suggestedEdits?.find(isRemoveTabEdit);

  /**
   * Uses the similar algorithm to the one in `./text`. It's not actually that
   * good an algorithm but let's keep it consistent and fix it later.
   *
   * It prefers insert suggestions over remove suggestions.
   *
   * TODO: Fix this so it supports insert and remove suggestions on the same tab.
   */

  const children = node.isPositional
    ? [
        new PositionalTab({
          alignment:
            node.options?.type === 'right' || node.options?.type === 'center'
              ? node.options?.type
              : 'left',
          leader: node.options?.leader || 'none',
          relativeTo: node.options?.relativeTo || 'indent',
        }),
      ]
    : [new Tab()];
  if (insertSuggestion) {
    return new InsertedTextRun({
      author: context.getAuthorName(insertSuggestion.authorId),
      children,
      date: getEditDate(insertSuggestion),
      id: context.generateEditId(insertSuggestion.groupId),
    });
  }
  if (removedSuggestion) {
    return new DeletedTextRun({
      author: context.getAuthorName(removedSuggestion.authorId),
      children,
      date: getEditDate(removedSuggestion),
      id: context.generateEditId(removedSuggestion.groupId),
    });
  }
  return new TextRun({ children });
}

export function createTabStops(tabOptions: TabOptions): TabStopDefinition[] {
  const { leader, position, type } = tabOptions;
  return [
    {
      leader: leader ?? 'none',
      position: position ?? 0,
      type: type ?? 'left',
    },
  ];
}
